import type { DbiDiscovery, ParcelSnapshot, SourceCoverage } from './types';

const SOURCES: SourceCoverage['source'][] = [
  'dbi',
  'assessor',
  'recorder',
  'subdivision',
  'sf311',
  'public-works',
  'section604',
  'fire-inspections',
  'fire-violations',
  'fire-compliance',
];

export const PUBLIC_SOURCE_URLS: Record<SourceCoverage['source'], string> = {
  dbi: 'https://dbiweb02.sfgov.org/dbipts/',
  assessor: 'https://sfplanninggis.org/pim/',
  recorder: 'https://recorder.sfgov.org/#!/simple',
  subdivision: 'https://bsm.sfdpw.org/subdivision/',
  'public-works': 'https://data.sf.gov/d/b6tj-gt35',
  sf311: 'https://data.sf.gov/d/vw6y-z8j6',
  section604: 'https://data.sf.gov/d/75h8-2zhw',
  'fire-inspections': 'https://data.sf.gov/d/wb4c-6hwj',
  'fire-violations': 'https://data.sf.gov/d/4zuq-2cbe',
  'fire-compliance':
    'https://sanfrancisco.agencycounter.com/search/saved/548a63ef-65dc-488b-88ad-30b8e0ed6e38',
};

export type PublicPreviewSnapshot = ParcelSnapshot & {
  preview: NonNullable<ParcelSnapshot['preview']>;
};

export { shouldCheckSubdivision } from '../skills/sf-subdivision/scripts/subdivision-relevance';

export function createPublicPreviewSnapshot(discovery: DbiDiscovery): PublicPreviewSnapshot {
  const recordDetails = Object.fromEntries(
    discovery.records.map((record) => [`${record.kind}:${record.id}`, 'pending' as const]),
  );
  const sourceStates = Object.fromEntries(
    SOURCES.map((source) => [source, { status: 'pending' as const }]),
  );
  return {
    parserVersion: discovery.parserVersion,
    block: discovery.block,
    lot: discovery.lot,
    addresses: discovery.addresses,
    fetchedAt: discovery.observedAt,
    sourceUrl: discovery.sourceUrl,
    records: discovery.records.map((record) => ({
      ...record,
      description: record.description ?? '',
      pages: [],
    })),
    health: {
      complete: false,
      fetchedPages: discovery.fetchedPages,
      expectedPages: discovery.fetchedPages + discovery.records.length,
      sources: [],
    },
    preview: {
      recordDetails,
      sourceStates,
      novsComplete: false,
      phase: 'listing',
    },
  };
}

/**
 * Every record detail and every checked source finished. Subdivision may be
 * skipped as irrelevant; a skipped source is not a gap in what was shown.
 */
export function previewCoverageComplete(snapshot: ParcelSnapshot) {
  const preview = snapshot.preview;
  if (!preview || preview.phase !== 'complete' || !preview.novsComplete) return false;
  if (!Object.values(preview.recordDetails).every((status) => status === 'complete')) return false;
  const sources = snapshot.health.sources ?? [];
  return SOURCES.every((source) => {
    const coverage = sources.filter((item) => item.source === source);
    if (coverage.length === 1) return coverage[0]?.status === 'complete';
    return coverage.length === 0 && preview.sourceStates[source]?.status === 'not-checked';
  });
}

/**
 * A progressive preview becomes a strict snapshot only when every source,
 * including subdivision, and every record detail completed.
 */
export function completedPreviewSnapshot(snapshot: ParcelSnapshot): ParcelSnapshot | undefined {
  if (!previewCoverageComplete(snapshot)) return undefined;
  const sources = snapshot.health.sources ?? [];
  if (sources.length !== SOURCES.length) return undefined;
  const { preview: _, ...rest } = snapshot;
  return { ...rest, health: { ...snapshot.health, complete: true } };
}
